import { zodResolver } from '@hookform/resolvers/zod';
import { isToday } from 'date-fns';
import { FC, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Blocker, Link, useBlocker, useNavigate, useParams } from 'react-router-dom';

import {
	Alert,
	AlertBody,
	AlertDescription,
	AlertDialog,
	AlertDialogBody,
	AlertDialogCloseButton,
	AlertDialogContent,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogOverlay,
	AlertIcon,
	ArrowLeftOutline,
	Button,
	Display,
	HStack,
	ListItem,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Spacer,
	Stack,
	Text,
	UnorderedList,
	useDisclosure,
	useToast,
} from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';
import { Schema } from '@bonusly/schema';
import { useAnalytics } from '@bonusly/who-am-i';

import { PageTitle } from '../../components/page/page-title';
import { FORM_PANEL_WIDTH } from '../../config/admin';
import { CelebrationWorkAnniversaryPointSetting } from '../../entities/celebrations';
import { useCelebration, useCelebrationRecordMetadata, useUpdateCelebration } from '../../queries/celebrations';
import { useUserGroups } from '../../queries/groups';
import { CelebrationForm } from './celebration-form';
import { DeactivateCelebrationModal } from './deactivate-celebration-modal';
import * as events from './events';
import { useDefaultFormValue } from './hooks';
import { CelebrationFormData } from './types';
import { buildUpdateFormBody, createSchema } from './utils';

export const EditCelebration: FC = () => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'edit' });
	const { id } = useParams<{ id: string }>();
	const toast = useToast();
	const navigate = useNavigate();
	const { track } = useAnalytics();
	const { data: rules } = useUserGroups();
	const formDataRef = useRef<CelebrationFormData>();
	const cancelRef = useRef<HTMLButtonElement>(null);

	assertPresence(id);
	assertPresence(rules);

	const { data: metadata } = useCelebrationRecordMetadata(id);
	const { data: record } = useCelebration(id);

	assertPresence(metadata);
	assertPresence(record);

	const confirmModal = useDisclosure();
	const deleteModal = useDisclosure();
	const reactivateModal = useDisclosure({ defaultIsOpen: record.isInactive });
	const resetFormValues = useDefaultFormValue(record.type);

	const isCustomCelebrationDateToday = record.isCustom && record.celebrationDate != null && isToday(record.celebrationDate);

	const { disabledValues, forceScope } = useMemo(() => {
		if (!metadata.restrictedConditions.length) {
			return { disabledValues: [], forceScope: undefined };
		}

		const existingScope = metadata.restrictedConditions.find((condition) => condition.celebrationId !== id);

		if (!existingScope) {
			return { disabledValues: [], forceScope: undefined };
		}

		const forceScope = existingScope.condition.attr;
		const disabledValues = metadata.restrictedConditions.filter((c) => c.celebrationId !== id).map((c) => c.condition.value);

		return { disabledValues, forceScope };
	}, []);

	const defaultValues = useMemo(() => {
		const setDefaultEnabled = (ruleAttr: string) => {
			if (!record.receivingRulesGroup) {
				return [];
			}

			return record.receivingRulesGroup.conditions.filter((condition) => condition.attr === ruleAttr).map((condition) => condition.value);
		};

		const defaultWorkAnniversaryPointSetting = (() => {
			if (record.yearAmountMappings.length == 0) {
				return CelebrationWorkAnniversaryPointSetting.EveryYearWithSameAmount;
			} else if (!record.amount) {
				return CelebrationWorkAnniversaryPointSetting.OnlySpecificYears;
			} else {
				return CelebrationWorkAnniversaryPointSetting.EveryYearWithCustomAmount;
			}
		})();

		return {
			celebrationDate: record.celebrationDate,
			icon: record.iconUrl,
			image: record.imageUrl,
			headline: record.headline,
			name: record.name,
			amount: record.amount,
			reason: record.reason,
			recipientDepartments: setDefaultEnabled('department'),
			recipientLocations: setDefaultEnabled('location'),
			recipientTeams: setDefaultEnabled('manager_id'),
			type: record.type,
			visibility: record.visibility,
			yearAmountMappings: record.yearAmountMappings.map<{ yearThreshold: number; amount: number }>(Schema.serialize),
			workAnniversaryPointSetting: defaultWorkAnniversaryPointSetting,
		};
	}, [record, rules]);

	const form = useForm<CelebrationFormData>({
		defaultValues,
		resolver: zodResolver(createSchema({ forceScope })),
	});

	const { mutate, isLoading } = useUpdateCelebration(record, {
		onMutate: () => {
			track(...events.edit(record));
		},
		onSuccess: (record) => {
			toast({
				title: t('updateSuccess', { name: record.name }),
				status: 'success',
			});
			navigate('/admin/programs/celebrations');
		},
		onError: () => {
			track(...events.editFailure(record));

			toast({
				title: t('updateError'),
				status: 'error',
			});
		},
	});

	const blocker = useBlocker(({ currentLocation, nextLocation }) => {
		return !isLoading && form.formState.isDirty && currentLocation !== nextLocation;
	});

	useEffect(() => {
		const handleBeforeUnload = (e: BeforeUnloadEvent) => {
			if (form.formState.isDirty) {
				e.preventDefault();
				e.returnValue = '';
			}
		};

		window.addEventListener('beforeunload', handleBeforeUnload);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
		};
	}, [form]);

	const handleRevertToDefault = () => {
		track(...events.reset(record));
		form.reset(resetFormValues);
		reactivateModal.onClose();
	};

	const handleRestore = () => {
		reactivateModal.onClose();
		track(...events.reactivate(record));
	};

	const handleConfirmUpdate = (data: CelebrationFormData) => {
		formDataRef.current = data;
		confirmModal.onOpen();
	};

	const handleSubmitForm = () => {
		if (formDataRef.current) {
			mutate(buildUpdateFormBody(formDataRef.current));
		}
	};

	return (
		<>
			<PageTitle>{t('title')}</PageTitle>

			<Stack px={10} py={8} spacing={6} maxW={FORM_PANEL_WIDTH}>
				<HStack>
					<Button as={Link} variant="link" gap={1} to="/admin/programs/celebrations" type="button" onClick={() => navigate('/admin/programs/celebrations')}>
						<ArrowLeftOutline size="xs" />
						{t('backButton')}
					</Button>
				</HStack>

				<Display size="sm">{t('display')}</Display>

				{isCustomCelebrationDateToday && (
					<Alert status="error">
						<AlertIcon />
						<AlertBody>
							<AlertDescription>{t('customCelebrationDateToday')}</AlertDescription>
						</AlertBody>
					</Alert>
				)}

				<CelebrationForm
					disabledValues={disabledValues}
					forceScope={forceScope}
					form={form}
					isLoading={isLoading}
					isSubmitDisabled={!form.formState.isDirty}
					onSubmit={handleConfirmUpdate}
					onDelete={deleteModal.onOpen}
					isDisabled={isCustomCelebrationDateToday}
				/>
			</Stack>

			<DeactivateCelebrationModal celebration={record} disclosure={deleteModal} />
			<ConfirmUpdateModal form={form} isLoading={isLoading} modal={confirmModal} onConfirm={handleSubmitForm} />

			<Modal isOpen={reactivateModal.isOpen} onClose={reactivateModal.onClose} size="lg">
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>{t('reactivateTitle')}</ModalHeader>
					<ModalCloseButton />
					<ModalBody p={5}>
						<Text mb={2}>{t('reactivateDescription1', { name: record.name })}</Text>
						<Text>{t('reactivateDescription2')}</Text>
					</ModalBody>

					<ModalFooter>
						<Button variant="ghost" mr={2} onClick={handleRevertToDefault}>
							{t('reactivateCancel')}
						</Button>
						<Button onClick={handleRestore}>{t('reactivateConfirm')}</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>

			<UnsavedChangesModal blocker={blocker} cancelRef={cancelRef} />
		</>
	);
};

type ConfirmUpdateModalProps = {
	form: ReturnType<typeof useForm<CelebrationFormData>>;
	isLoading: boolean;
	modal: ReturnType<typeof useDisclosure>;
	onConfirm: () => void;
};

type FieldSet = Exclude<keyof CelebrationFormData, 'type'>;

const fieldMap: Record<FieldSet, string> = {
	name: 'nameLabel',
	celebrationDate: 'postOnLabel',
	visibility: 'postVisibilityLabel',
	recipientDepartments: 'recipientLabel',
	recipientLocations: 'recipientLabel',
	recipientTeams: 'recipientLabel',
	amount: 'pointsLabel',
	yearAmountMappings: 'pointsLabel',
	workAnniversaryPointSetting: 'pointsLabel',
	icon: 'postCustomization.iconLabel',
	headline: 'postCustomization.headlineLabel',
	reason: 'postCustomization.messageLabel',
	image: 'postCustomization.imageLabel',
};

export const ConfirmUpdateModal: FC<ConfirmUpdateModalProps> = ({ form, isLoading, modal, onConfirm }) => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'edit' });
	const { t: tForm } = useTranslation('celebrations', { keyPrefix: 'form' });
	const edited = Object.entries(fieldMap)
		.filter(([key]) => {
			const dirtyField = form.formState.dirtyFields[key as FieldSet];
			if (Array.isArray(dirtyField)) {
				return !!dirtyField.length;
			} else {
				return !!dirtyField;
			}
		})
		.map(([, key]) => key);

	const keys = [...new Set(edited)];

	return (
		<Modal isOpen={modal.isOpen} onClose={modal.onClose} size="lg">
			<ModalOverlay />
			<ModalContent>
				<ModalHeader data-testid="confirm-changes-title">{t('confirmTitle')}</ModalHeader>
				<ModalCloseButton />
				<ModalBody p={5}>
					<Text mb={2}>{t('confirmDescription')}</Text>
					<UnorderedList pl={2}>
						{keys.map((key, i) => (
							<ListItem key={i}>{tForm(key)}</ListItem>
						))}
					</UnorderedList>
				</ModalBody>

				<ModalFooter>
					<Button variant="ghost" mr={2} onClick={modal.onClose}>
						{t('confirmCancel')}
					</Button>
					<Spacer />
					<Button isLoading={isLoading} onClick={onConfirm}>
						{t('confirmConfirm')}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

const UnsavedChangesModal: FC<{
	blocker: Blocker;
	cancelRef: React.RefObject<HTMLButtonElement>;
}> = ({ blocker, cancelRef }) => {
	const { t } = useTranslation('celebrations', { keyPrefix: 'edit' });

	return (
		<AlertDialog leastDestructiveRef={cancelRef} isOpen={blocker.state === 'blocked'} onClose={() => blocker.reset?.()}>
			<AlertDialogOverlay>
				<AlertDialogContent>
					<AlertDialogHeader fontSize="lg" fontWeight="bold">
						{t('unsavedChangesTitle')}
					</AlertDialogHeader>
					<AlertDialogCloseButton />

					<AlertDialogBody>{t('unsavedChangesDescription')}</AlertDialogBody>

					<AlertDialogFooter>
						<Button colorScheme="secondary" variant="alpha" ref={cancelRef} onClick={() => blocker.reset?.()}>
							{t('unsavedChangesContinueEditing')}
						</Button>
						<Spacer />
						<Button colorScheme="primary" variant="solid" onClick={() => blocker.proceed?.()}>
							{t('unsavedChangesDiscardChanges')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialogOverlay>
		</AlertDialog>
	);
};
